import {
  BooleanField,
  BooleanInput,
  BulkDeleteButton,
  ChipField,
  Create,
  Datagrid,
  DateField,
  DateTimeInput,
  Edit,
  FunctionField,
  List,
  Resource,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  useRecordContext,
} from "react-admin";
import { CrudPermissions } from "../../auth/crud-permissions";
import {
  ConversionInputStreamResource,
  ConversionInputStreamSourceList,
  inputStreamAdAccountPlatform,
  inputStreamConnectionType,
} from "@arrowsup/platform-dtos";
import InputIcon from "@mui/icons-material/Input";
import { omit } from "../../../logic/omit";
import {
  formatTimestamp,
  parseTimestamp,
} from "../../../logic/api/timestamp-codec";
import { TopHelpInfoText } from "../../help/top-help-info-text";
import { SourceConfig } from "./source-configs/source-config";

export const ConversionInputStream = (perms: CrudPermissions) => {
  return (
    <Resource
      name="conversion-input-stream"
      key="conversion-input-stream"
      options={{ label: "Input Streams" }}
      list={ConversionInputStreamList(perms)}
      create={perms.create ? ConversionInputStreamCreate() : undefined}
      edit={perms.update ? ConversionInputStreamEdit() : undefined}
      icon={InputIcon}
    />
  );
};

const ConversionInputStreamListInner: React.FC<{
  perms: CrudPermissions;
}> = ({ perms }) => {
  return (
    <>
      <TopHelpInfoText text="Pull conversions from external sources like Dialpad call logs, Leap CRM records, or Google Local Services Ads leads." />
      <List>
        <Datagrid
          rowClick={perms.update ? "edit" : false}
          bulkActionButtons={perms.delete ? <BulkDeleteButton /> : false}
        >
          <TextField source="id" label="Stream ID" />
          <TextField source="description" label="Description" />
          <ChipField source="source.name" label="Source" />
          <TextField
            source="integrationConnection.name"
            label="Connection"
            emptyText="—"
            sortable={false}
          />
          <TextField
            source="adAccount.externalId"
            label="Ad Account"
            emptyText="—"
            sortable={false}
          />
          <BooleanField source="enabled" />
          <FunctionField
            label="Start Time"
            render={(record: { startTimeMs?: number | null }) =>
              record.startTimeMs
                ? new Date(record.startTimeMs).toLocaleString()
                : "—"
            }
          />
          <FunctionField
            label="Last Ingested"
            render={(record: { lastIngestedEventTimeMs?: number | null }) =>
              record.lastIngestedEventTimeMs
                ? new Date(record.lastIngestedEventTimeMs).toLocaleString()
                : "—"
            }
          />
          <DateField source="createdAt" label="Created" />
          <DateField source="updatedAt" label="Last Updated" />
        </Datagrid>
      </List>
    </>
  );
};

export const ConversionInputStreamList = (perms: CrudPermissions) => {
  return <ConversionInputStreamListInner perms={perms} />;
};

/** `leap-appointment-set` -> `Leap Appointment Set`. */
const sourceLabel = (source: string): string =>
  source
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const SourceChooser: React.FC = () => {
  return (
    <SelectInput
      source="source.name"
      // eslint-disable-next-line
      choices={ConversionInputStreamSourceList.map((s) => ({
        // eslint-disable-next-line
        id: s,
        // eslint-disable-next-line
        name: sourceLabel(s),
      }))}
      required
      fullWidth
    />
  );
};

const StartTimeInput = () => {
  const record = useRecordContext();
  const isNew = record?.id === undefined;

  return (
    <DateTimeInput
      label="Start Time"
      source="startTimeMs"
      parse={parseTimestamp}
      format={formatTimestamp}
      helperText="Events before this time will not be ingested. Cannot be edited after the stream is created."
      fullWidth
      required
      disabled={!isNew}
    />
  );
};

/**
 * The stream to save: its connection and ad account by id alone, and none for
 * a source that doesn't read through one (say, after switching source).
 */
const withAccountIds = <
  T extends Pick<
    Partial<ConversionInputStreamResource>,
    "source" | "integrationConnection" | "adAccount"
  >
>(
  stream: T
): T => {
  const sourceName = stream.source?.name ?? "";
  const connectionId = stream.integrationConnection?.id;
  const adAccountId = stream.adAccount?.id;
  return {
    ...stream,
    integrationConnection:
      inputStreamConnectionType(sourceName) && connectionId
        ? { id: connectionId }
        : null,
    adAccount:
      inputStreamAdAccountPlatform(sourceName) && adAccountId
        ? { id: adAccountId }
        : null,
  };
};

export const ConversionInputStreamCreate = () => {
  return (
    <Create redirect="list" transform={withAccountIds}>
      <SimpleForm>
        <TextInput source="description" fullWidth />
        <BooleanInput fullWidth source="enabled" />
        <StartTimeInput />
        <SourceChooser />
        <SourceConfig />
      </SimpleForm>
    </Create>
  );
};

export const ConversionInputStreamEdit = () => {
  return (
    <Edit
      redirect="list"
      transform={(data: ConversionInputStreamResource) =>
        withAccountIds(omit(data, ["createdAt", "updatedAt", "startTimeMs"]))
      }
    >
      <SimpleForm>
        <TextInput source="description" fullWidth />
        <BooleanInput fullWidth source="enabled" />
        <StartTimeInput />
        <SourceChooser />
        <SourceConfig />
      </SimpleForm>
    </Edit>
  );
};
